import { Link } from "react-router-dom";
import { ExternalLink } from "lucide-react";
import { imgUrl } from "@/lib/api";
import { storeLabel } from "@/constants/search";
import { useLocale } from "@/context/LocaleContext";
import { AffiliateLinkNote } from "@/components/AffiliateDisclosure";

export default function CompareProductCard({ product, index = 0, instant = false }) {
  const { formatMoney } = useLocale() || { formatMoney: (n) => `$${n}` };
  const img = imgUrl(product.image || product.images?.[0]);
  const eager = instant && index < 8;
  const href = `/product/${encodeURIComponent(product.id)}`;
  const buy = product.buy_url && /^https?:/i.test(product.buy_url) ? product.buy_url : href;
  const offsite = Boolean(product.buy_url && /^https?:/i.test(product.buy_url));
  const india = product.source === "india_crawlee";
  const affiliate = Boolean(product.affiliate);
  const store = storeLabel(product);
  const openStore = india && offsite;

  const frame = "relative block aspect-[3/4] w-full overflow-hidden bg-white";
  const photo = img ? (
    <img
      referrerPolicy="no-referrer"
      src={img}
      alt={product.title}
      className="absolute inset-0 h-full w-full object-cover"
      loading={eager ? "eager" : "lazy"}
      fetchPriority={eager ? "high" : "auto"}
      decoding="async"
    />
  ) : (
    <div className="absolute inset-0 shimmer" />
  );
  const titleClass = "block min-h-[2.5rem] font-serif text-base leading-tight line-clamp-2 hover:text-aura-accent";

  return (
    <article
      className={`group flex h-full flex-col ${instant ? "" : "fade-up"}`}
      style={instant ? undefined : { animationDelay: `${Math.min(index, 8) * 50}ms` }}
      data-testid={`product-card-${product.id}`}
    >
      {openStore ? (
        <a href={buy} target="_blank" rel={affiliate ? "sponsored noopener" : "noreferrer"} className={frame}>
          {photo}
        </a>
      ) : (
        <Link to={href} state={{ product }} className={frame}>
          {photo}
          {product.offer_count > 1 && (
            <span className="absolute bottom-2 left-2 glass text-[10px] tracking-luxe uppercase px-2 py-1">
              {product.offer_count} stores
            </span>
          )}
        </Link>
      )}
      <div className="mt-3 flex min-h-[4.75rem] items-start justify-between gap-3">
        <div className="min-w-0">
          {openStore ? (
            <a href={buy} target="_blank" rel={affiliate ? "sponsored noopener" : "noreferrer"} className={titleClass}>
              {product.title}
            </a>
          ) : (
            <Link to={href} className={titleClass}>
              {product.title}
            </Link>
          )}
          <p className="mt-0.5 h-4 truncate text-xs text-aura-muted">{store}</p>
        </div>
        <div className="mt-0.5 shrink-0 text-right font-mono text-sm">
          {product.price != null
            ? formatMoney(product.price, product.currency || "USD")
            : product.source === "involve_asia" ? "" : "—"}
        </div>
      </div>
      {offsite ? (
        <a
          href={buy}
          target="_blank"
          rel={affiliate ? "sponsored noopener" : "noreferrer"}
          className="mt-3 inline-flex h-10 w-full items-center justify-center gap-1.5 text-sm font-medium btn-accent"
        >
          {store === "eBay" ? "Buy on eBay" : india ? `Buy on ${store}` : product.source === "involve_asia" ? "Shop now" : "Buy now"} <ExternalLink className="w-3.5 h-3.5" />
        </a>
      ) : (
        <Link to={href} className="mt-3 inline-flex h-10 w-full items-center justify-center text-sm font-medium btn-accent">
          Buy now
        </Link>
      )}
      {affiliate ? <AffiliateLinkNote className="mt-2" /> : null}
    </article>
  );
}
